/**
 * Map Firebase auth error codes to user-friendly messages.
 */
export function mapAuthError(code: string): string {
  const errorMap: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password. Please try again.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Contact support.',
  };

  return errorMap[code] || 'An error occurred during sign in. Please try again.';
}

/**
 * Map Firebase auth error codes to user-friendly messages for the password
 * reset flow. Separate from `mapAuthError` because the sign-in copy (and its
 * "during sign in" fallback) reads wrong when the user asked for a reset link.
 *
 * Every branch returns a safe, user-facing string — callers must never surface
 * the raw Firebase error, its code, or its stack.
 */
export function mapPasswordResetError(code: string): string {
  const errorMap: Record<string, string> = {
    'auth/missing-email': 'No email is associated with this account.',
    'auth/invalid-email': 'The email on this account is not a valid address.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/user-disabled': 'This account has been disabled. Contact support.',
    'auth/too-many-requests': 'Too many reset requests. Please wait a few minutes and try again.',
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  };

  return errorMap[code] || 'Could not send the password reset email. Please try again.';
}
